import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Search, X, PackageCheck, TicketPercent, RefreshCw, Gift } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GiftCardSellModal from "@/components/pos/GiftCardSellModal";
import { toast } from "sonner";
import CardChargeModal from "@/components/pos/CardChargeModal";
import { fmtDate, money } from "@/lib/format";

const TENDER_TYPES = [
  { v: "cash", l: "Cash" },
  { v: "card_manual", l: "Card (enter here)" },
  { v: "card_external", l: "Card (terminal)" },
  { v: "gift_card", l: "Gift card" },
  { v: "stripe", l: "Stripe (link/QR)" },
];

function POSProductVariantTile({ product, onAdd }) {
  const [sel, setSel] = useState(product.variants[0]?.name || "");
  const variant = product.variants.find((v) => v.name === sel);
  const price = Number(product.price || 0) + Number(variant?.price_delta || 0);
  return (
    <div className="rounded-xl border border-[#E5E0D8] px-3 py-2.5" data-testid={`pos-product-variant-${product.id}`}>
      <span className="text-sm text-[#1A242B] block truncate">{product.title}</span>
      <div className="flex items-center gap-2 mt-1.5">
        <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid={`pos-variant-select-${product.id}`}
          className="flex-1 rounded-lg border border-[#E5E0D8] px-2 py-1 text-xs outline-none focus:border-[#144F75] bg-white">
          {product.variants.map((v) => (
            <option key={v.name} value={v.name}>{v.name}{Number(v.price_delta) ? ` (${Number(v.price_delta) > 0 ? "+" : ""}${money(Number(v.price_delta))})` : ""}</option>
          ))}
        </select>
        <span className="text-xs text-[#144F75] w-14 text-right">{money(price)}</span>
        <button onClick={() => onAdd(product, variant)} data-testid={`pos-add-product-${product.id}`}
          className="eyw-btn !py-1 !px-2 text-xs"><Plus size={14} /></button>
      </div>
    </div>
  );
}

export default function POSRegister() {
  const [client, setClient] = useState(null);
  const [clientQ, setClientQ] = useState("");
  const [birthday, setBirthday] = useState(null);
  const [lines, setLines] = useState([]);
  const [assigned, setAssigned] = useState("");
  const [assignedAuto, setAssignedAuto] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [tip, setTip] = useState(0);
  const [tipTo, setTipTo] = useState("");
  const [giftCode, setGiftCode] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount, label }
  const [couponBusy, setCouponBusy] = useState(false);
  const [redeemCodes, setRedeemCodes] = useState([]);
  const [tenderType, setTenderType] = useState("cash");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [cardSale, setCardSale] = useState(null);
  const [custom, setCustom] = useState({ title: "", price: "" });
  const [pkgConfirm, setPkgConfirm] = useState(null); // { chosen: creditId }
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pkgSuccess, setPkgSuccess] = useState(null);

  const catalogLive = { refetchInterval: 30000, refetchOnWindowFocus: true };
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const refreshCatalog = async () => {
    setRefreshing(true);
    try {
      await Promise.all(["pos-services", "pos-products", "pos-packages", "pos-events-upcoming",
        "pos-assignable", "pos-tip-recipients"].map((k) => qc.refetchQueries({ queryKey: [k] })));
      toast.success("Catalog refreshed — showing the latest center data.");
    } catch { toast.error("Could not refresh. Check your connection."); }
    finally { setRefreshing(false); }
  };
  const stripeCfg = useQuery({ queryKey: ["pos-stripe-config"], queryFn: () => api.get("/pos/stripe/config").then((r) => r.data) });
  const tipRecipients = useQuery({ queryKey: ["pos-tip-recipients"], queryFn: () => api.get("/pos/tip-recipients").then((r) => r.data.recipients || []), ...catalogLive });
  const assignable = useQuery({ queryKey: ["pos-assignable"], queryFn: () => api.get("/pos/assignable").then((r) => r.data.people || []), ...catalogLive });

  const services = useQuery({ queryKey: ["pos-services"], queryFn: () => api.get("/pos/services").then((r) => r.data), ...catalogLive });
  const products = useQuery({ queryKey: ["pos-products"], queryFn: () => api.get("/pos/products").then((r) => r.data), ...catalogLive });
  const packages = useQuery({ queryKey: ["pos-packages"], queryFn: () => api.get("/pos/packages").then((r) => r.data), ...catalogLive });
  const events = useQuery({ queryKey: ["pos-events-upcoming"], queryFn: () => api.get("/pos/events-upcoming").then((r) => r.data), ...catalogLive });
  const clients = useQuery({
    queryKey: ["pos-client-search", clientQ],
    queryFn: () => api.get(`/pos/clients?q=${encodeURIComponent(clientQ)}`).then((r) => r.data),
    enabled: clientQ.length >= 2,
  });

  const [sellGift, setSellGift] = useState(false);
  const addLine = (kind, obj) => {
    const price = obj.price ?? obj.unit_price ?? 0;
    setLines((l) => [...l, { kind, ref_id: obj.id || "", title: obj.title || obj.name, unit_price: price, qty: 1 }]);
  };
  const addProduct = (p, variant) => {
    if (variant && variant.name) {
      const unit = Number(p.price || 0) + Number(variant.price_delta || 0);
      setLines((l) => [...l, { kind: "product", ref_id: p.id, title: `${p.title} — ${variant.name}`, unit_price: unit, qty: 1, variant: { name: variant.name, price_delta: Number(variant.price_delta || 0) } }]);
    } else {
      addLine("product", p);
    }
  };
  const rmLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));
  const showGiftCodes = async (saleId) => {
    try {
      const { data: s2 } = await api.get(`/pos/sales/${saleId}`);
      const codes = s2.gift_card_codes || [];
      if (codes.length) toast.success(`Gift card${codes.length > 1 ? "s" : ""} created: ${codes.join(", ")} — emailed to the purchaser`, { duration: 20000 });
    } catch { /* codes are still emailed */ }
  };
  const setQty = (i, q) => setLines((l) => l.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, Number(q) || 1) } : x));
  const setLineEnh = (i, v) => setLines((l) => l.map((x, idx) => idx === i ? { ...x, enhancer_id: v } : x));

  const subtotal = lines.reduce((s, x) => s + x.unit_price * x.qty, 0);
  const couponAmt = coupon ? Number(coupon.discount || 0) : 0;
  const total = Math.max(subtotal - Number(discount || 0) - couponAmt - (birthday?.amount || 0), 0) + Number(tip || 0);

  const couponItems = () => lines.map((x) => ({ kind: x.kind, ref_id: x.ref_id, title: x.title, qty: x.qty, unit_price: x.unit_price, ...(x.variant ? { variant: x.variant } : {}) }));
  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    if (lines.length === 0) { toast.error("Add items to the sale first."); return; }
    setCouponBusy(true);
    try {
      const { data } = await api.post("/pos/coupon-preview", { code, client_id: client?.id || "", items: couponItems() });
      if (data.discount > 0) {
        setCoupon(data);
        toast.success(`${data.label} applied — saves ${money(Number(data.discount))}${data.message ? `. ${data.message}` : ""}`);
      } else {
        setCoupon(null);
        toast.error(data.message || "That coupon can't be applied.");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not check that code."); }
    finally { setCouponBusy(false); }
  };
  // Re-quote the applied coupon + birthday perk whenever the ticket or client changes.
  useEffect(() => {
    if (lines.length === 0 || (!coupon && !client)) { setCoupon(coupon && lines.length ? coupon : null); setBirthday(null); if (lines.length === 0) setCoupon(null); return; }
    let live = true;
    api.post("/pos/coupon-preview", { code: coupon?.code || "", client_id: client?.id || "", items: couponItems() })
      .then(({ data }) => {
        if (!live) return;
        if (coupon) setCoupon(data.discount > 0 ? data : null);
        setBirthday(data.birthday || null);
      })
      .catch(() => { if (live) { if (coupon) setCoupon(null); setBirthday(null); } });
    return () => { live = false; };
  }, [lines, client, coupon?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Package credit payment: available when a client is attached and the ticket is a single service.
  const svcLine = lines.length === 1 && lines[0].kind === "service" ? lines[0] : null;
  const pkgCreditsQ = useQuery({
    queryKey: ["pos-applicable-credits", client?.id, svcLine?.ref_id],
    queryFn: () => api.get(`/pos/clients/${client.id}/applicable-credits`, { params: { service_id: svcLine.ref_id } }).then((r) => r.data.credits || []),
    enabled: !!client && !!svcLine,
  });
  const pkgCredits = pkgCreditsQ.data || [];
  const clientSummaryQ = useQuery({
    queryKey: ["pos-client-summary", client?.id],
    queryFn: () => api.get(`/pos/clients/${client.id}/summary`).then((r) => r.data),
    enabled: !!client,
  });
  const clientSummary = clientSummaryQ.data;

  const redeemPackage = async () => {
    const chosen = pkgCredits.find((c) => c.id === pkgConfirm?.chosen) || pkgCredits[0];
    if (!chosen) return;
    setPkgLoading(true);
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const { data } = await api.post("/pos/package-redemption", {
        client_id: client.id, credit_id: chosen.id, service_id: svcLine.ref_id,
        checkout: true,
        date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
        start_time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
        enhancer_id: assigned || "",
      });
      setPkgConfirm(null);
      setPkgSuccess({ package_name: data.package_name, remaining: data.sessions_remaining, service: svcLine.title });
      toast.success(`Package redemption complete — ${data.package_name}, ${data.sessions_remaining} session${data.sessions_remaining === 1 ? "" : "s"} remaining.`);
      reset();
      setClient(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiError(typeof d === "object" ? d.message : d) || err.message);
    } finally { setPkgLoading(false); }
  };

  const addGift = async () => {
    if (!giftCode) return;
    const code = giftCode.toUpperCase();
    try {
      const { data } = await api.get(`/pos/giftcards/${encodeURIComponent(code)}`);
      setRedeemCodes((c) => [...new Set([...c, code])]);
      setGiftCode("");
      toast.success(`Gift card ${code} — ${money(Number(data.balance))} available`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gift card not found or inactive");
    }
  };

  const pickClient = async (c) => {
    setClient(c); setClientQ("");
    // Booking redemption: pre-populate the assignment from the client's booking today.
    try {
      const { data } = await api.get(`/pos/clients/${c.id}/todays-enhancer`);
      if (data?.enhancer_id) {
        setAssigned(data.enhancer_id);
        setAssignedAuto(`${data.enhancer_name} — from today's ${data.service_title || "booking"}`);
      }
    } catch { /* optional */ }
  };

  const complete = async () => {
    if (lines.length === 0) { toast.error("Add at least one item."); return; }
    const hasService = lines.some((x) => x.kind === "service" && !x.enhancer_id);
    if (hasService && !assigned) { toast.error("Assign the enhancer responsible for this service sale (ticket-level or per line)."); return; }
    const person = (assignable.data || []).find((p) => p.id === assigned);
    setLoading(true);
    setCheckout(null);
    try {
      const isCardManual = tenderType === "card_manual";
      const payload = {
        client_id: client?.id || "",
        assigned_to_id: assigned,
        assigned_to_name: person?.name || "",
        assigned_to_type: person?.type || "",
        items: lines.map((x) => ({ kind: x.kind, ref_id: x.ref_id, title: x.title, qty: x.qty, unit_price: x.unit_price, ...(x.enhancer_id ? { enhancer_id: x.enhancer_id } : {}), ...(x.variant ? { variant: x.variant } : {}), ...(x.gift || {}) })),
        discounts: [
          ...(Number(discount) > 0 ? [{ kind: "manual", amount: Number(discount), reason: discountReason }] : []),
          ...(coupon ? [{ kind: "coupon", code: coupon.code, amount: 0 }] : []),
          ...(birthday ? [{ kind: "birthday", amount: 0 }] : []),
        ],
        redemptions: redeemCodes.map((c) => ({ gift_card_code: c })),
        tip: Number(tip || 0),
        tip_to_enhancer_id: Number(tip || 0) > 0 ? tipTo : "",
        tip_to_name: Number(tip || 0) > 0 && tipTo ? ((tipRecipients.data || []).find((r) => r.id === tipTo)?.name || "") : "",
        tenders: isCardManual
          ? []
          : tenderType === "stripe"
          ? [{ type: "stripe", amount: total }]
          : [{ type: tenderType, amount: total, ref: tenderType === "gift_card" ? (redeemCodes[0] || "") : "" }],
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/pos/sales", payload);
      if (isCardManual) {
        if (data.sale.payment_status === "paid") {
          toast.success(`Sale paid — ${money(data.sale.total)}`);
          reset();
        } else {
          setCardSale(data.sale); // opens the card-entry modal
        }
      } else if (data.checkout_url) {
        setCheckout(data.checkout_url);
        toast.info("Stripe payment link created — scan the QR or open the link.");
      } else {
        toast.success(`Sale ${data.sale.status} — ${money(data.sale.total)}`);
        if (lines.some((x) => x.kind === "giftcard")) showGiftCodes(data.sale.id);
        reset();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiError(typeof d === "object" ? d.message : d) || err.message);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setLines([]); setDiscount(0); setDiscountReason(""); setTip(0); setTipTo("");
    setRedeemCodes([]); setCheckout(null); setAssigned(""); setAssignedAuto("");
    setCoupon(null); setCouponCode(""); setBirthday(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6 [&>*]:min-w-0" data-testid="pos-register">
      {/* Catalog */}
      <div className="space-y-6">
        <button onClick={() => setSellGift(true)} data-testid="pos-sell-giftcard" className="eyw-card w-full p-4 flex items-center justify-between hover:border-[#D98E73] transition-colors text-left">
          <span className="flex items-center gap-3"><span className="grid place-items-center h-10 w-10 rounded-full bg-[#D98E73]/15"><Gift size={18} className="text-[#D98E73]" /></span>
            <span><span className="block font-serif text-lg text-[#1A242B]">Sell a gift card</span><span className="block text-xs text-[#4A5D6B]">Preset amounts or any custom amount</span></span></span>
          <Plus size={18} className="text-[#144F75]" />
        </button>
        {sellGift && <GiftCardSellModal client={client} onAdd={(ln) => setLines((l) => [...l, ln])} onClose={() => setSellGift(false)} />}
        <div className="eyw-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg text-[#1A242B]">Services</h3>
            <button onClick={refreshCatalog} disabled={refreshing} data-testid="pos-refresh-catalog"
              title="Pull the latest services, prices, packages and enhancers"
              className="inline-flex items-center gap-1.5 text-xs text-[#144F75] border border-[#E5E0D8] rounded-full px-3 py-1.5 hover:border-[#144F75] transition-colors disabled:opacity-60">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(services.data || []).map((s) => (
              <button key={s.id} onClick={() => addLine("service", s)} data-testid={`pos-add-service-${s.id}`}
                className="text-left min-w-0 rounded-xl border border-[#E5E0D8] px-3 py-2.5 hover:border-[#144F75] transition-colors">
                <span className="text-sm text-[#1A242B] block truncate">{s.title}</span>
                <span className="text-xs text-[#144F75]">{money(s.price)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="eyw-card p-5">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Packages</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {(packages.data || []).map((p) => (
              <button key={p.id} onClick={() => addLine("package", p)} data-testid={`pos-add-package-${p.id}`}
                className="text-left min-w-0 rounded-xl border border-[#E5E0D8] px-3 py-2.5 hover:border-[#144F75] transition-colors">
                <span className="text-sm text-[#1A242B] block truncate">{p.name}</span>
                <span className="text-xs text-[#144F75]">{money(p.price)} · {p.sessions} sessions</span>
              </button>
            ))}
          </div>
        </div>
        <div className="eyw-card p-5">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Products</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {(products.data || []).map((p) => (
              (p.variants && p.variants.length > 0)
                ? <POSProductVariantTile key={p.id} product={p} onAdd={addProduct} />
                : <button key={p.id} onClick={() => addProduct(p)} data-testid={`pos-add-product-${p.id}`}
                    className="text-left min-w-0 rounded-xl border border-[#E5E0D8] px-3 py-2.5 hover:border-[#144F75] transition-colors">
                    <span className="text-sm text-[#1A242B] block truncate">{p.title}</span>
                    <span className="text-xs text-[#144F75]">{money(p.price)}</span>
                  </button>
            ))}
          </div>
        </div>
        <div className="eyw-card p-5">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Event tickets</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {(events.data || []).map((e) => (
              <button key={e.id} onClick={() => addLine("ticket", { id: e.id, title: e.title, price: Number(e.price || 0) })} data-testid={`pos-add-ticket-${e.id}`}
                className="text-left min-w-0 rounded-xl border border-[#E5E0D8] px-3 py-2.5 hover:border-[#144F75] transition-colors">
                <span className="text-sm text-[#1A242B] block truncate">{e.title}</span>
                <span className="text-xs text-[#144F75]">{money(Number(e.price || 0))} · {fmtDate(e.date)}</span>
              </button>
            ))}
            {(events.data || []).length === 0 && <p className="text-xs text-[#8FA89B] col-span-full">No upcoming events.</p>}
          </div>
          <p className="text-[11px] text-[#8FA89B] mt-2">Selling a ticket registers the attached client on the event roster and emails them a confirmation.</p>
        </div>
        <div className="eyw-card p-5">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Custom charge / donation</h3>
          <div className="flex gap-2">
            <input placeholder="Description" value={custom.title} onChange={(e) => setCustom({ ...custom, title: e.target.value })} data-testid="pos-custom-title" className="eyw-input flex-1" />
            <input type="number" placeholder="$" value={custom.price} onChange={(e) => setCustom({ ...custom, price: e.target.value })} data-testid="pos-custom-price" className="eyw-input w-24" />
            <button onClick={() => { if (custom.title && custom.price) { addLine("custom", { title: custom.title, unit_price: Number(custom.price) }); setCustom({ title: "", price: "" }); } }} data-testid="pos-add-custom" className="eyw-btn !px-3"><Plus size={16} /></button>
          </div>
        </div>
      </div>

      {/* Ticket */}
      <div className="eyw-card p-6 h-fit lg:sticky lg:top-24" data-testid="pos-ticket">
        {/* Client */}
        <div className="mb-4">
          {client ? (
            <>
              <div className="flex items-center justify-between rounded-xl bg-[#144F75]/5 px-3 py-2">
                <span className="text-sm text-[#1A242B]" data-testid="pos-selected-client">{client.first_name} {client.last_name}</span>
                <button onClick={() => setClient(null)} className="text-[#4A5D6B]"><X size={16} /></button>
              </div>
              {clientSummary?.birthday_week && (
                <div className="mt-2 rounded-xl bg-[#D98E73]/10 border border-[#D98E73]/30 px-3 py-2 text-xs text-[#8A5A3B]" data-testid="pos-birthday-banner">
                  🎂 It's {client.first_name}'s birthday week — wish them well!
                  {clientSummary?.birthday_perk && <span className="block mt-0.5">{clientSummary.birthday_perk.label}{clientSummary.birthday_perk.note ? ` · ${clientSummary.birthday_perk.note}` : ""} — applies automatically at checkout.</span>}
                </div>
              )}
            </>
          ) : (
            <div className="relative">
              <div className="flex items-center gap-2 eyw-input">
                <Search size={15} className="text-[#8FA89B]" />
                <input placeholder="Attach client (optional)" value={clientQ} onChange={(e) => setClientQ(e.target.value)} data-testid="pos-client-search" className="bg-transparent outline-none flex-1 text-sm" />
              </div>
              {clients.data?.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl shadow-lg max-h-52 overflow-y-auto">
                  {clients.data.map((c) => (
                    <button key={c.id} onClick={() => pickClient(c)} data-testid={`pos-client-opt-${c.id}`} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#F9F7F3]">
                      {c.first_name} {c.last_name} <span className="text-xs text-[#8FA89B]">{c.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assigned enhancer / vendor */}
        <div className="mb-4" data-testid="pos-assigned-block">
          <label className="text-xs text-[#4A5D6B] block mb-1">
            Sale by {lines.some((x) => x.kind === "service") ? <span className="text-[#D98E73] font-medium">(required for services)</span> : <span className="text-[#8FA89B]">(optional)</span>}
          </label>
          <select value={assigned} onChange={(e) => { setAssigned(e.target.value); setAssignedAuto(""); }} data-testid="pos-assigned-select" className="eyw-input w-full text-sm">
            <option value="">— Assign enhancer / vendor —</option>
            <optgroup label="Enhancers">
              {(assignable.data || []).filter((p) => p.type === "enhancer").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
            <optgroup label="Vendors">
              {(assignable.data || []).filter((p) => p.type === "vendor").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          </select>
          {assignedAuto && <p className="text-[11px] text-[#5C8A72] mt-1" data-testid="pos-assigned-auto">Auto-filled: {assignedAuto}</p>}
        </div>

        <h3 className="font-serif text-xl text-[#1A242B] mb-3">Sale</h3>
        {lines.length === 0 ? <p className="text-sm text-[#4A5D6B] py-6 text-center">No items yet. Tap catalog items to add.</p> : (
          <ul className="space-y-2 mb-4" data-testid="pos-cart-lines">
            {lines.map((x, i) => (
              <li key={i} className="text-sm" data-testid={`pos-line-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate text-[#1A242B]">{x.title}</span>
                  <input type="number" value={x.qty} onChange={(e) => setQty(i, Number(e.target.value))} className="w-14 eyw-input !py-1 text-center" />
                  <span className="w-16 text-right text-[#144F75]">${(x.unit_price * x.qty).toFixed(2)}</span>
                  <button onClick={() => rmLine(i)} data-testid={`pos-rm-line-${i}`} className="text-red-500"><Trash2 size={14} /></button>
                </div>
                {["service", "product", "custom", "addon"].includes(x.kind) && (
                  <select value={x.enhancer_id || ""} onChange={(e) => setLineEnh(i, e.target.value)} data-testid={`pos-line-enh-${i}`}
                    className={`mt-1 w-full text-xs rounded-lg border bg-transparent px-2 py-1 ${x.enhancer_id ? "border-[#5C8A72]/50 text-[#5C8A72]" : "border-[#E5E0D8] text-[#8FA89B]"}`}>
                    <option value="">{assigned ? `↳ ${(assignable.data || []).find((p) => p.id === assigned)?.name || "ticket default"}` : "↳ same as 'Sale by'"}</option>
                    {(assignable.data || []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.type === "vendor" ? " (vendor)" : ""}</option>)}
                  </select>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2 border-t border-[#E5E0D8] pt-3">
          <div className="flex items-center gap-2">
            <input placeholder="Gift card code" value={giftCode} onChange={(e) => setGiftCode(e.target.value)} data-testid="pos-gift-code" className="eyw-input flex-1 !py-1.5 text-sm" />
            <button onClick={addGift} data-testid="pos-apply-gift" className="eyw-btn-ghost !py-1.5 !px-3 text-xs">Redeem</button>
          </div>
          {redeemCodes.length > 0 && <p className="text-xs text-[#5C8A72]">Redeeming: {redeemCodes.join(", ")}</p>}
          <div className="flex items-center gap-2">
            <input placeholder="Coupon code" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} data-testid="pos-coupon-code" className="eyw-input flex-1 !py-1.5 text-sm uppercase font-mono" />
            <button onClick={applyCoupon} disabled={couponBusy} data-testid="pos-apply-coupon" className="eyw-btn-ghost !py-1.5 !px-3 text-xs disabled:opacity-50">{couponBusy ? <Loader2 className="animate-spin" size={13} /> : "Apply"}</button>
          </div>
          {coupon && (
            <p className="text-xs text-[#5C8A72] flex items-center gap-1.5" data-testid="pos-coupon-applied">
              <TicketPercent size={13} /> {coupon.label} — saves ${Number(coupon.discount).toFixed(2)}
              {coupon.message && <span className="text-[#B5794E]">({coupon.message})</span>}
              <button onClick={() => { setCoupon(null); setCouponCode(""); }} data-testid="pos-coupon-remove" className="text-[#4A5D6B] hover:text-red-600"><X size={12} /></button>
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#4A5D6B]">Discount $</label>
              <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="pos-discount" className="eyw-input w-full !py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-[#4A5D6B]">Tip $</label>
              <input type="number" value={tip} onChange={(e) => setTip(e.target.value)} data-testid="pos-tip" className="eyw-input w-full !py-1.5 text-sm" />
            </div>
          </div>
          {Number(discount) > 0 && <input placeholder="Discount reason" value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} data-testid="pos-discount-reason" className="eyw-input w-full !py-1.5 text-sm" />}
          {Number(tip) > 0 && (
            <div data-testid="pos-tip-to-wrap">
              <label className="text-xs text-[#4A5D6B]">Tip goes to</label>
              <select value={tipTo} onChange={(e) => setTipTo(e.target.value)} data-testid="pos-tip-to" className="eyw-input w-full !py-1.5 text-sm">
                <option value="">The center (unassigned)</option>
                {(tipRecipients.data || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="border-t border-[#E5E0D8] mt-3 pt-3 space-y-1">
          <div className="flex justify-between text-sm text-[#4A5D6B]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          {Number(discount) > 0 && <div className="flex justify-between text-sm text-[#4A5D6B]"><span>Discount</span><span>-${Number(discount).toFixed(2)}</span></div>}
          {coupon && <div className="flex justify-between text-sm text-[#5C8A72]" data-testid="pos-coupon-row"><span>Coupon · {coupon.code}</span><span>-${Number(coupon.discount).toFixed(2)}</span></div>}
          {birthday && <div className="flex justify-between text-sm text-[#D98E73]" data-testid="pos-birthday-row"><span>🎂 {birthday.label}</span><span>-{money(Number(birthday.amount))}</span></div>}
          {Number(tip) > 0 && <div className="flex justify-between text-sm text-[#4A5D6B]"><span>Tip{tipTo ? ` · ${(tipRecipients.data || []).find((r) => r.id === tipTo)?.name || ""}` : ""}</span><span>{money(Number(tip))}</span></div>}
          <div className="flex justify-between font-serif text-xl text-[#1A242B]"><span>Total</span><span data-testid="pos-total">{money(total)}</span></div>
        </div>

        <div className="mt-4">
          <label className="text-xs text-[#4A5D6B]">Payment method</label>
          <select value={tenderType} onChange={(e) => setTenderType(e.target.value)} data-testid="pos-tender" className="eyw-input w-full">
            {TENDER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>

        <button onClick={complete} disabled={loading} data-testid="pos-complete" className="eyw-btn w-full mt-4 disabled:opacity-60">
          {loading && <Loader2 className="animate-spin" size={18} />} Complete sale
        </button>

        {/* Pay with package credit */}
        {client && svcLine && (
          <div className="mt-2">
            <button onClick={() => setPkgConfirm({ chosen: pkgCredits[0]?.id })} disabled={pkgCredits.length === 0 || pkgLoading}
              data-testid="pos-pay-package"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#5C8A72] px-6 py-2.5 text-[#5C8A72] font-medium text-sm transition-colors hover:bg-[#5C8A72]/10 disabled:opacity-45 disabled:cursor-not-allowed">
              <PackageCheck size={16} /> Pay with Package Credit
            </button>
            {pkgCredits.length === 0 && !pkgCreditsQ.isLoading && (
              <p className="text-[11px] text-[#8FA89B] text-center mt-1" data-testid="pos-no-credit-msg">No package credits available for this service</p>
            )}
          </div>
        )}
        {pkgSuccess && (
          <div className="mt-3 rounded-xl border border-[#5C8A72]/40 bg-[#5C8A72]/10 p-3 text-sm text-[#1A242B]" data-testid="pos-package-success">
            ✓ <b>{pkgSuccess.service}</b> paid with a <b>{pkgSuccess.package_name}</b> credit — {pkgSuccess.remaining} session{pkgSuccess.remaining === 1 ? "" : "s"} remaining.
            <button onClick={() => setPkgSuccess(null)} className="float-right text-[#4A5D6B]"><X size={14} /></button>
          </div>
        )}

        {checkout && (
          <div className="mt-4 text-center border-t border-[#E5E0D8] pt-4" data-testid="pos-stripe-qr">
            <p className="text-sm text-[#4A5D6B] mb-2">Scan to pay by card, or open on a phone:</p>
            <img alt="Payment QR" className="mx-auto rounded-lg border border-[#E5E0D8]" src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(checkout)}`} />
            <a href={checkout} target="_blank" rel="noreferrer" className="eyw-btn-ghost !py-2 text-sm mt-3 inline-block">Open payment page</a>
          </div>
        )}
      </div>

      {pkgConfirm && (() => {
        const chosen = pkgCredits.find((c) => c.id === pkgConfirm.chosen) || pkgCredits[0];
        return (
          <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={() => setPkgConfirm(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="pos-package-confirm">
              <h3 className="font-serif text-xl text-[#1A242B] mb-2">Pay with package credit</h3>
              {pkgCredits.length > 1 && (
                <select value={pkgConfirm.chosen || pkgCredits[0].id} onChange={(e) => setPkgConfirm({ chosen: e.target.value })}
                  data-testid="pos-package-select" className="eyw-input w-full text-sm mb-3">
                  {pkgCredits.map((c) => <option key={c.id} value={c.id}>{c.package_name} — {c.sessions_remaining}/{c.sessions_total} left{c.expires_at ? ` · expires ${c.expires_at}` : ""}</option>)}
                </select>
              )}
              <p className="text-sm text-[#4A5D6B] mb-1">
                Apply 1 credit from <b className="text-[#1A242B]">{chosen?.package_name}</b>? <b className="text-[#1A242B]">{chosen?.sessions_remaining} session{chosen?.sessions_remaining === 1 ? "" : "s"} remaining.</b>
              </p>
              <p className="text-xs text-[#8FA89B] mb-5">Covers “{svcLine?.title}” — recorded as a package redemption, no charge.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPkgConfirm(null)} data-testid="pos-package-cancel" className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
                <button onClick={redeemPackage} disabled={pkgLoading} data-testid="pos-package-confirm-btn" className="eyw-btn !py-2 text-sm !bg-[#5C8A72] hover:!bg-[#4d7561] disabled:opacity-60">
                  {pkgLoading && <Loader2 className="animate-spin" size={15} />} Confirm redemption
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {cardSale && (
        <CardChargeModal
          sale={cardSale}
          client={client}
          publishableKey={stripeCfg.data?.publishable_key}
          onClose={() => setCardSale(null)}
          onPaid={(sale) => {
            toast.success(`Card charged — ${money(Number(sale.total))}`);
            setCardSale(null);
            reset();
          }}
        />
      )}
    </div>
  );
}
